import {
  AxisIndustryId,
  AxisSubIndustryId,
  CLIENT_TYPE_IDS,
  CLIENT_TYPE_OPTIONS,
  ClientType,
  COMMERCIAL_INDUSTRY_OPTIONS,
  LWB_INDUSTRY_OPTIONS,
  PERSONAL_INDUSTRY_OPTIONS,
  SelectableOption,
  commercialIndustryRequiresSubIndustry,
  getCommercialSubIndustryOptions,
} from './axisNewClientConstants';

export type AxisNewClientDeepLinkStep =
  | 'clientType'
  | 'industry'
  | 'subIndustry'
  | 'form';

export type AxisNewClientDeepLinkState = {
  step: AxisNewClientDeepLinkStep;
  clientType: ClientType;
  industryId: AxisIndustryId | null;
  subIndustryId: AxisSubIndustryId | null;
};

// Maps normalized incoming param values to their canonical equivalents.
// Use this when Axis changes the public-facing URL param names without
// changing the underlying store values.
const PARAM_ALIASES: Record<
  'type' | 'industry' | 'sub_industry',
  Record<string, string>
> = {
  type: {
    LBW: 'LWB',
  },
  industry: {
    GROUP_BENEFITS: 'BENEFITS',
    GROUP_RETIREMENT: 'WEALTH_INSURANCE',
  },
  sub_industry: {
    CONCRETERS: 'CONCRETORS',
  },
};

const normalizeDeepLinkValue = (
  value: string | null,
  param?: keyof typeof PARAM_ALIASES,
): string | null => {
  const normalized = value?.trim().toUpperCase();
  if (!normalized) {
    return null;
  }
  if (param) {
    return PARAM_ALIASES[param][normalized] ?? normalized;
  }
  return normalized;
};

const findOptionIdByParam = <OptionId extends string>(
  options: Array<SelectableOption<OptionId>>,
  value: string | null,
  param?: keyof typeof PARAM_ALIASES,
): OptionId | null => {
  const normalized = normalizeDeepLinkValue(value, param);
  if (!normalized) {
    return null;
  }

  return options.find((option) => option.id === normalized)?.id ?? null;
};

const getIndustryOptionsForClientType = (
  clientType: ClientType,
): Array<SelectableOption<AxisIndustryId>> => {
  switch (clientType) {
    case CLIENT_TYPE_IDS.commercial:
      return COMMERCIAL_INDUSTRY_OPTIONS;
    case CLIENT_TYPE_IDS.personal:
      return PERSONAL_INDUSTRY_OPTIONS;
    case CLIENT_TYPE_IDS.lwb:
      return LWB_INDUSTRY_OPTIONS;
    default:
      throw new Error(`Unsupported Axis client type: ${clientType}`);
  }
};

export const parseAxisNewClientDeepLinkParams = (
  params: URLSearchParams,
): AxisNewClientDeepLinkState | null => {
  const clientType = findOptionIdByParam(
    CLIENT_TYPE_OPTIONS,
    params.get('type'),
    'type',
  );

  if (!clientType) {
    return null;
  }

  const industryId = findOptionIdByParam(
    getIndustryOptionsForClientType(clientType),
    params.get('industry'),
    'industry',
  );

  if (!industryId) {
    return {
      step: 'industry',
      clientType,
      industryId: null,
      subIndustryId: null,
    };
  }

  if (
    clientType !== CLIENT_TYPE_IDS.commercial ||
    !commercialIndustryRequiresSubIndustry(industryId)
  ) {
    return {
      step: 'form',
      clientType,
      industryId,
      subIndustryId: null,
    };
  }

  const subIndustryId = findOptionIdByParam(
    getCommercialSubIndustryOptions(industryId),
    params.get('sub_industry'),
    'sub_industry',
  );

  if (!subIndustryId) {
    return {
      step: 'subIndustry',
      clientType,
      industryId,
      subIndustryId: null,
    };
  }

  return {
    step: 'form',
    clientType,
    industryId,
    subIndustryId,
  };
};

export type AxisNewClientDeepLinkInput = {
  clientType: string;
  industryId?: string | null;
  subIndustryId?: string | null;
};

export const buildAxisNewClientDeepLinkParams = ({
  clientType,
  industryId,
  subIndustryId,
}: AxisNewClientDeepLinkInput): URLSearchParams => {
  const params = new URLSearchParams();
  params.set('type', clientType);
  if (industryId) {
    params.set('industry', industryId);
  }
  if (subIndustryId) {
    params.set('sub_industry', subIndustryId);
  }

  return params;
};
